import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {MetaService} from '../../../../meta/service/meta.service';
import {CoreMetaExternalSideCarService} from '../../../../meta/service/core-meta-external-side-car.service';
import {MetaDataEntityDto} from '../../../../meta/dto/meta-data-entity.dto';
import {MetaDataAttributeDto} from '../../../../meta/dto/meta-data-attribute.dto';
import {MetaDataAttributeKind} from '../../../../meta/logic/meta-data-attribute-kind';
import {MetaDataJoinRelationEnum} from '../../../../meta/enum/meta-data-join-relation.enum';
import {Sorting} from '../../../../misc/sorting';
import {FilterCriteria} from '../../../../filter/api/filter.criteria';
import {FilterCriteriaChangeEvent} from '../../../../filter/api/filter-criteria-change-event';
import {WidgetData} from '../../../../widget/widget-data';
import {MvsFormDto} from '../../../../dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from '../../../../dto/form/dto/mvs-form-field.dto';
import {MvsFormContentDto} from '../../../../dto/form/dto/mvs-form-content.dto';
import {MvsFormValueListDto} from '../../../../dto/form/dto/mvs-form-value-list.dto';
import {MvsFormValueListEntryDto} from '../../../../dto/form/dto/mvs-form-value-list-entry.dto';
import {QlRequestDto} from '../../../data/dto/ql-request.dto';
import {
    QlBuilderDateGroupPrecision,
    QlBuilderGroupByAggregateFunction,
    QlBuilderGroupByAggregateModel,
    QlBuilderGroupByFieldModel,
    QlBuilderJoinNode,
    QlBuilderModel,
} from '../../../builder/ql-builder-model';
import {QL_BUILDER_POLICY_LIMITS, QlBuilderQueryConverter} from '../../../builder/ql-builder-query-converter';
import {QlBuilderRelativeDate} from '../../../builder/ql-builder-relative-date';
import {QlBuilderContainerNode, QlBuilderNodeContext, QlBuilderTreeOps} from '../logic/ql-builder-tree-ops';
import {QlBuilderKeyLabels} from '../logic/ql-builder-key-labels';
import {QlBuilderNodeComponent} from '../ql-builder-node/ql-builder-node.component';
import {QlBuilderPanelFieldOption} from '../interface/ql-builder-panel-field-option.interface';
import {QlBuilderPanelOption} from '../interface/ql-builder-panel-option.interface';
import {QlBuilderKeyOption} from '../interface/ql-builder-key-option.interface';
import {QlBuilderFieldOption} from '../interface/ql-builder-field-option.interface';
import {QlBuilderRelativeDateDraft} from '../interface/ql-builder-relative-date-draft.interface';
import {QlBuilderRelativeFilterChip} from '../interface/ql-builder-relative-filter-chip.interface';
import {QlBuilderConditionHints} from '../interface/ql-builder-condition-hints.interface';

/**
 * The QL-Widget-Builder panel: start entity (`mvs-object-type`), the recursive node tree
 * (`mvs-ql-builder-node`), global sorting, limit and the "Gruppieren/Aggregieren" mode with its
 * grouping fields (plain or dateGroup day/month/year) and aggregates (count/sum/avg/min/max).
 *
 * Generic on purpose (no dependency on the ui module): the model goes in via {@link model} and every
 * edit leaves as a NEW model object via {@link modelChange} — the host decides where it goes (the
 * workbench page hands it to `UiQlWidgetWorkbenchStateService.setModel`).
 *
 * Declared and exported by `QlBuilderModule` (NgModule, fe_core_standards), which
 * `UiQlWidgetBuilderModule` imports; `shared.module.ts` stays untouched.
 */
@Component({
    selector: 'mvs-ql-builder-panel',
    templateUrl: './ql-builder-panel.component.html',
    styleUrls: ['./ql-builder-panel.component.scss'],
    standalone: false,
})
export class QlBuilderPanelComponent implements OnChanges, OnDestroy {

    static readonly TEXT_ENV_BOUND =
        'Die Abfrage enthält generische Felder oder externe Zusatzfelder, die über IDs angesprochen werden. '
        + 'Sie funktioniert nur in dieser Umgebung und lässt sich nicht übertragen.';

    /** A condition on a field of a LEFT join outside an OR group / IS NULL turns the join into a required one. */
    static readonly TEXT_CONDITION_LEFT_JOIN =
        'Wirkt wie eine Pflicht-Verknüpfung (Inner Join): Datensätze ohne verknüpften Eintrag fallen weg. '
        + 'Nur die verknüpften Zeilen einschränken? Dann den Filter an der Verknüpfung setzen.';

    static readonly TEXT_CONDITION_ONE_TO_N = 'Bedingung auf 1:n-Verknüpfung: Zeilen können sich vervielfachen.';

    static readonly TEXT_CONDITIONS_EMPTY =
        'Keine Bedingungen – hier lassen sich Felder der Start-Entität und aller Verknüpfungen filtern, auch kombiniert mit ODER.';

    static readonly TEXT_CONDITIONS_SCOPE =
        'Filterbar sind die Start-Entität und ihre Verknüpfungen. Nicht filterbar: Verknüpfungen mit „nur erster Eintrag“ '
        + '(und alles darunter), erweiterte (berechnete) Felder und externe Zusatzfelder.';

    @Input() model: QlBuilderModel | null = null;

    @Output() modelChange = new EventEmitter<QlBuilderModel | null>();

    readonly textEnvBound = QlBuilderPanelComponent.TEXT_ENV_BOUND;
    readonly limitsDefault = QL_BUILDER_POLICY_LIMITS.defaultPreviewPageSize;

    readonly precisionOptions: QlBuilderPanelOption<QlBuilderDateGroupPrecision | null>[] = [
        {label: 'exakter Wert', value: null},
        {label: 'je Tag', value: 'day'},
        {label: 'je Monat', value: 'month'},
        {label: 'je Jahr', value: 'year'},
    ];

    readonly functionOptions: QlBuilderPanelOption<QlBuilderGroupByAggregateFunction>[] = [
        {label: 'Anzahl', value: 'count'},
        {label: 'Summe', value: 'sum'},
        {label: 'Durchschnitt', value: 'avg'},
        {label: 'Minimum', value: 'min'},
        {label: 'Maximum', value: 'max'},
    ];

    readonly directionOptions: QlBuilderPanelOption<boolean>[] = [
        {label: 'aufsteigend', value: true},
        {label: 'absteigend', value: false},
    ];

    envBound: boolean = false;
    rootContext: QlBuilderNodeContext | null = null;
    groupByBlockedReason: string | null = null;

    /**
     * Hint shown after "Gruppieren/Aggregieren" was switched on (QA #5/#20): whether sortings were dropped by
     * the switch, and which join fields now act as ADDITIONAL grouping keys (`joinKeys`, recomputed live from
     * the model so it empties once the fields are removed). `null` in row mode.
     */
    groupByHint: { sortingsRemoved: boolean; joinKeys: string[] } | null = null;
    /**
     * Q3-8: labels of the joins that carry nothing any more after "Zusätzliche Schlüssel entfernen"
     * ({@link QlBuilderTreeOps.emptyJoins}) - offered for removal with "Leere Verknüpfung entfernen";
     * `null` when there is none or the offer was not triggered by that action.
     */
    emptyJoinHint: string[] | null = null;
    /** Sortable result keys: German label from the metadata/model, the key as secondary text. */
    sortOptions: QlBuilderKeyOption[] = [];
    maxLimit: number = QL_BUILDER_POLICY_LIMITS.maxPagingEntity;

    rootMeta: MetaDataEntityDto | null = null;
    rootMetaState: 'none' | 'loading' | 'ready' | 'error' = 'none';

    // ---- query-level conditions ("Bedingungen (alle Entitäten)") ----

    readonly textConditionsEmpty = QlBuilderPanelComponent.TEXT_CONDITIONS_EMPTY;
    readonly textConditionsScope = QlBuilderPanelComponent.TEXT_CONDITIONS_SCOPE;
    readonly textConditionLeftJoin = QlBuilderPanelComponent.TEXT_CONDITION_LEFT_JOIN;
    readonly textConditionOneToN = QlBuilderPanelComponent.TEXT_CONDITION_ONE_TO_N;
    readonly textRelativeDate = QlBuilderNodeComponent.TEXT_RELATIVE_DATE;

    /** Readable text per AND-linked top-level condition (relative dates are chips instead; a day range reads as one). */
    conditionsSummary: string[] = [];
    /** Relative date conditions (`${D-…}`), shown as chips with their index in `model.conditions`. */
    conditionRelativeChips: QlBuilderRelativeFilterChip[] = [];
    /** The open "Relativer Zeitraum" editor of the conditions card, `null` when closed. */
    conditionRelativeDraft: QlBuilderRelativeDateDraft | null = null;
    /** Date fields of every filterable node, values ALREADY qualified (`j1.createdDate`), labels "Typ: Erstellt am". */
    conditionDateFieldOptions: QlBuilderFieldOption[] = [];
    /** Hints for the conditions of the model (card). */
    conditionHints: QlBuilderConditionHints = {leftJoin: [], oneToN: []};
    /** Hints for the conditions currently edited in the dialog (draft), falling back to the model's. */
    conditionDialogHints: QlBuilderConditionHints = {leftJoin: [], oneToN: []};
    /** Number of nodes a condition may filter on (root + regular joins outside onlyFirst subtrees). */
    filterableNodeCount: number = 0;

    conditionsDialogVisible: boolean = false;
    /** The dialog builds its form from the per-alias metadata; 'loading' while a joined entity's metadata is still on its way. */
    conditionsDialogState: 'none' | 'loading' | 'ready' | 'error' = 'none';
    conditionsDialogCriteria: FilterCriteria[] = [];
    /**
     * Client-built `MvsFormDto` in the shape `/core/ql/template` delivers (field ids `<alias>.<field>`,
     * `formContents` `{as, objectType}`), so the shared `mvs-condition-builder` runs in QL mode with ONE
     * field tree over all filterable aliases — no template round trip, and only the aliases the policy
     * allows a query-level filter on.
     */
    conditionsDialogForm: MvsFormDto | null = null;
    conditionsDialogWidgetData: WidgetData | null = null;
    /** The condition builder's last change (criteria + validity), `null` until the user edits something. */
    conditionsDraft: { criteria: FilterCriteria[]; valid: boolean } | null = null;

    /**
     * Rebuild key of the start-entity input (N6): `mvs-object-type` only ever SETS its text for a non-empty
     * alias, so an emptied model (reset, preset 8) left the old entity visible. Bumped whenever the model
     * goes from an entity to empty; the template tracks the input by it, which recreates it blank.
     */
    startEntityKey: number = 0;
    groupFieldOptions: QlBuilderPanelFieldOption[] = [];
    aggregateOptionsByFn: { [fn in QlBuilderGroupByAggregateFunction]: QlBuilderPanelOption<string>[] } =
        {count: [], sum: [], avg: [], min: [], max: []};

    private metaSubscription: Subscription | null = null;
    private loadedRootAlias: string | null = null;
    /**
     * Set when the join fields of a grouping model were removed - by {@link clearJoinFields} here or by the
     * same button of the widget-type editor (the model then goes from join keys to none): from then on the
     * joins left empty are offered for removal.
     */
    private offerEmptyJoinRemoval: boolean = false;
    private lastJoinKeyCount: number = 0;

    /** Metadata of the joined entities, only for the German labels of the sort keys (root: {@link rootMeta}). */
    private readonly labelMetas = new Map<string, MetaDataEntityDto>();
    private readonly labelMetaRequested = new Set<string>();
    /** Entities whose metadata could not be loaded — never re-requested, their fields are simply not offered. */
    private readonly labelMetaFailed = new Set<string>();
    private readonly labelMetaSubscriptions = new Subscription();

    /**
     * TypeField labels per side-car version (`QlBuilderKeyLabels.sideCarKey`) for the sort keys of a LOADED
     * definition (N7) - a side-car applied in this session carries its labels in the model already.
     */
    private readonly sideCarFieldLabels = new Map<string, ReadonlyMap<number, string>>();
    private readonly sideCarLabelRequested = new Set<string>();

    constructor(private readonly metaService: MetaService,
                private readonly sideCarService: CoreMetaExternalSideCarService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        const alias = this.model?.root?.entityName ?? null;
        if (alias !== this.loadedRootAlias) {
            this.loadRootMeta(alias);
        }
        const previous = changes['model']?.previousValue as QlBuilderModel | null | undefined;
        if (changes['model'] && previous?.root?.entityName && !this.model) {
            this.startEntityKey++;
        }
        // an edited relative condition that moved or vanished (another edit replaced the model) closes its editor
        const draft = this.conditionRelativeDraft;
        if (changes['model'] && draft?.index != null && this.model?.conditions?.[draft.index] !== draft.criterion) {
            this.conditionRelativeDraft = null;
        }
        this.recompute();
    }

    ngOnDestroy(): void {
        this.metaSubscription?.unsubscribe();
        this.labelMetaSubscriptions.unsubscribe();
    }

    get groupByMode(): boolean {
        return !!this.model?.groupBy;
    }

    // ------------------------------------------------------------------
    // start entity + tree
    // ------------------------------------------------------------------

    /** `mvs-object-type` also emits for the alias it was handed — only a DIFFERENT entity starts over. */
    onStartEntityChange(alias: string): void {
        if (!alias || alias === this.model?.root?.entityName) {
            return;
        }
        this.emit(QlBuilderTreeOps.createModel(alias));
    }

    clearModel(): void {
        this.emit(null);
    }

    /** A root filter reaching into a join alias is a query-level condition — moved there, never left as `e.j1.x`. */
    onRootChange(root: QlBuilderContainerNode): void {
        if (this.model && root.kind === 'entity') {
            this.emit(QlBuilderTreeOps.normalizeRootFilters(QlBuilderTreeOps.withRoot(this.model, root)));
        }
    }

    // ------------------------------------------------------------------
    // query-level conditions ("Bedingungen (alle Entitäten)")
    // ------------------------------------------------------------------

    get hasConditions(): boolean {
        return (this.model?.conditions?.length ?? 0) > 0;
    }

    /** The conditions the dialog edits: everything but relative dates (chips) and criteria the form cannot show. */
    private dialogConditions(form: MvsFormDto | null): FilterCriteria[] {
        return (this.model?.conditions ?? []).filter(c => this.isDialogCondition(c, form));
    }

    private isDialogCondition(criterion: FilterCriteria, form: MvsFormDto | null): boolean {
        if (QlBuilderRelativeDate.isRelativeCriterion(criterion)) {
            return false;
        }
        const fields = form?.formFields as unknown as { [id: string]: MvsFormFieldDto } | undefined;
        return !fields || QlBuilderQueryConverter.leafCriteria(criterion).every(leaf => !!fields[leaf.field]);
    }

    openConditionsDialog(): void {
        if (!this.model?.root?.entityName) {
            return;
        }
        this.conditionsDraft = null;
        this.conditionsDialogForm = null;
        this.conditionsDialogWidgetData = null;
        this.conditionsDialogCriteria = [];
        this.conditionsDialogState = 'loading';
        this.conditionsDialogVisible = true;
        this.conditionDialogHints = this.conditionHints;
        this.prepareConditionsDialog();
    }

    /**
     * Builds the dialog once the metadata of EVERY filterable node is known (loaded, or failed for good).
     * Called on open and again on every metadata arrival while the dialog is loading.
     */
    private prepareConditionsDialog(): void {
        if (!this.conditionsDialogVisible || this.conditionsDialogState !== 'loading' || !this.model) {
            return;
        }
        const metas = this.currentLabelMetas();
        const info = QlBuilderKeyLabels.nodeInfo(this.model, metas);
        const pending = QlBuilderTreeOps.filterableNodes(this.model).some(node => {
            const entityAlias = info.get(node.alias)?.entityAlias;
            return !!entityAlias && !metas.has(entityAlias) && !this.labelMetaFailed.has(entityAlias);
        });
        if (pending || this.rootMetaState === 'loading') {
            return;
        }
        if (!this.rootMeta) {
            this.conditionsDialogState = 'error';
            return;
        }
        const form = this.buildConditionsForm(this.model, metas);
        this.conditionsDialogForm = form;
        this.conditionsDialogWidgetData = QlBuilderPanelComponent.createConditionsWidgetData(this.model);
        this.conditionsDialogCriteria = this.toConditionsDialogCriteria(this.dialogConditions(form));
        this.conditionsDialogState = 'ready';
    }

    /** `mvs-condition-builder` reports every edit with its validity — "Übernehmen" is gated on it. */
    onConditionsChange(event: FilterCriteriaChangeEvent): void {
        this.conditionsDraft = {criteria: event?.filterCriteria ?? [], valid: !!event?.valid};
        this.conditionDialogHints = this.hintsFor([
            ...this.fromConditionsDialogCriteria(this.conditionsDraft.criteria),
            ...this.preservedConditions(),
        ]);
    }

    get conditionsDialogApplicable(): boolean {
        return !!this.conditionsDraft?.valid;
    }

    /** "Übernehmen": the dialog rows replace the editable conditions; relative chips and non-editable ones survive. */
    applyConditionsDialog(): void {
        if (!this.conditionsDialogApplicable || !this.model) {
            return;
        }
        const next = [...this.fromConditionsDialogCriteria(this.conditionsDraft.criteria), ...this.preservedConditions()];
        this.closeConditionsDialog();
        this.emit(QlBuilderTreeOps.withConditions(this.model, next));
    }

    /** Conditions the dialog does not edit (relative dates, criteria its form cannot show). */
    private preservedConditions(): FilterCriteria[] {
        return (this.model?.conditions ?? []).filter(c => !this.isDialogCondition(c, this.conditionsDialogForm));
    }

    cancelConditionsDialog(): void {
        this.closeConditionsDialog();
    }

    /** `p-dialog` `visibleChange(false)` (close icon, Escape) is a cancel. */
    onConditionsDialogVisibleChange(visible: boolean): void {
        if (!visible && this.conditionsDialogVisible) {
            this.cancelConditionsDialog();
        }
    }

    private closeConditionsDialog(): void {
        this.conditionsDialogVisible = false;
        this.conditionsDialogState = 'none';
        this.conditionsDialogForm = null;
        this.conditionsDialogWidgetData = null;
        this.conditionsDraft = null;
    }

    /** "Entfernen": every query-level condition goes (the node filters stay untouched). */
    removeConditions(): void {
        if (this.model) {
            this.conditionRelativeDraft = null;
            this.emit(QlBuilderTreeOps.withConditions(this.model, null));
        }
    }

    // relative date conditions (chips, `${D-…}` on date fields of ANY filterable alias)

    openConditionRelativeDate(): void {
        if (this.conditionDateFieldOptions.length) {
            this.conditionRelativeDraft = {index: null, criterion: null};
        }
    }

    editConditionRelative(index: number): void {
        const criterion = this.model?.conditions?.[index];
        if (criterion && this.conditionRelativeChips.some(chip => chip.index === index && chip.editable)) {
            this.conditionRelativeDraft = {index, criterion};
        }
    }

    onConditionRelativeApply(criterion: FilterCriteria): void {
        const draft = this.conditionRelativeDraft;
        if (!draft || !criterion || !this.model) {
            return;
        }
        const conditions = [...(this.model.conditions ?? [])];
        if (draft.index != null && draft.index < conditions.length) {
            conditions[draft.index] = criterion;
        } else {
            conditions.push(criterion);
        }
        this.conditionRelativeDraft = null;
        this.emit(QlBuilderTreeOps.withConditions(this.model, conditions));
    }

    cancelConditionRelativeDate(): void {
        this.conditionRelativeDraft = null;
    }

    removeConditionRelative(index: number): void {
        if (!this.model) {
            return;
        }
        if (this.conditionRelativeDraft?.index === index) {
            this.conditionRelativeDraft = null;
        }
        this.emit(QlBuilderTreeOps.withConditions(this.model, (this.model.conditions ?? []).filter((_, i) => i !== index)));
    }

    /**
     * Dialog rows → conditions: the condition builder's field-less wrapper is dropped into a FLAT AND list (a
     * real OR group stays ONE group node, empty rows/groups vanish — same rules as the node dialog,
     * {@link QlBuilderNodeComponent.toAndList}); an absolute day picked on an `Instant`/`LocalDateTime` field
     * of ANY alias becomes whole-day bounds ({@link QlBuilderNodeComponent.normalizeDayCriterion}). Field ids
     * are the QL names already (`j1.name`, `e.g_4711:value`), so nothing is prefixed or mapped.
     */
    fromConditionsDialogCriteria(criteria: FilterCriteria[]): FilterCriteria[] {
        const clone = FilterCriteria.cloneFilterCriteriaArray(criteria) ?? [];
        const normalizeLeaf = (leaf: FilterCriteria): FilterCriteria[] =>
            QlBuilderNodeComponent.normalizeDayCriterion(leaf, this.conditionDateTimeKind(leaf.field));
        return clone.flatMap(c => QlBuilderNodeComponent.toAndList(c, normalizeLeaf));
    }

    /** Conditions → dialog rows: day ranges folded back, the flat list wrapped in an AND group (AND is the default). */
    toConditionsDialogCriteria(conditions: FilterCriteria[]): FilterCriteria[] {
        const rows = QlBuilderNodeComponent.foldDayRanges(FilterCriteria.cloneFilterCriteriaArray(conditions) ?? []);
        const wrapped = [QlBuilderNodeComponent.group('and', rows.length ? rows : [FilterCriteria.create('', FilterCriteria.cOperatorEqual, null)])];
        QlBuilderNodeComponent.ensureGroupArrays(wrapped);
        return wrapped;
    }

    /**
     * The form of the dialog, built on the client from the per-alias metadata: one `formContents` entry per
     * filterable node (`{as, objectType}` — what `MvsFilterHelper` maps the QL hierarchy with) and one form
     * field per filterable attribute of that node's entity, id `<alias>.<field>` with the attribute's German
     * label, Java `dataType` and value list — exactly what `/core/ql/template` would deliver for the SELECTED
     * members, extended here to ALL attributes. Never: extended (+/_) members, EAV, `ext_` side-car columns.
     * Generic attributes appear under their QL name `<alias>.g_<id>:value`.
     */
    buildConditionsForm(model: QlBuilderModel, metas: ReadonlyMap<string, MetaDataEntityDto>): MvsFormDto {
        const form = new MvsFormDto();
        const fields: { [id: string]: MvsFormFieldDto } = {};
        const contents: { [index: string]: MvsFormContentDto } = {};
        const info = QlBuilderKeyLabels.nodeInfo(model, metas);

        QlBuilderTreeOps.filterableNodes(model).forEach((node, index) => {
            const entityAlias = info.get(node.alias)?.entityAlias ?? null;
            const meta = entityAlias ? metas.get(entityAlias) ?? null : null;
            if (!entityAlias || !meta) {
                return;
            }
            const content = new MvsFormContentDto();
            content.id = index;
            content.as = node.alias;
            content.objectType = entityAlias;
            content.objectTypeId = meta.objectTypeId;
            content.objectTypeName = meta.entityLabel || entityAlias;
            content.entityLabel = meta.entityLabel;
            content.genericType = false;
            contents[String(index)] = content;

            for (const attribute of meta.attributes ?? []) {
                if (!attribute?.attributeName
                    || MetaDataAttributeKind.isExtendedAttribute(attribute)
                    || MetaDataAttributeKind.isEavAttribute(attribute)
                    || attribute.attributeName.startsWith('ext_')) {
                    continue;
                }
                const field = QlBuilderPanelComponent.conditionFormField(node.alias, attribute, index);
                fields[field.id] = field;
            }
        });

        form.formFields = fields as unknown as MvsFormFieldDto[];
        form.formContents = contents as unknown as MvsFormContentDto[];
        form.formComplete = true;
        form.formActions = [];
        return form;
    }

    /** One form field in the `/core/ql/template` shape for an attribute under a node alias. */
    static conditionFormField(alias: string, attribute: MetaDataAttributeDto, formContentId: number): MvsFormFieldDto {
        const field = new MvsFormFieldDto();
        field.id = `${alias}.${MetaDataAttributeKind.scalarFieldName(attribute)}`;
        field.uiLabel = attribute.label || attribute.attributeName;
        field.dataType = attribute.dataType;
        field.access = 'read';
        field.primaryKey = !!attribute.isPrimaryKey;
        field.uiInputControl = QlBuilderPanelComponent.inputControlOf(attribute.dataType);
        (field as unknown as { formContentId: number }).formContentId = formContentId;
        if (attribute.valueListEntries?.length) {
            field.valueList = MvsFormValueListDto.create(attribute.valueListEntries.map(entry => {
                const e = new MvsFormValueListEntryDto();
                e.key = entry.key;
                e.label = entry.label;
                e.technicalKey = entry.technicalLabel;
                return e;
            }));
        }
        return field;
    }

    private static inputControlOf(dataType: string | null | undefined): string {
        const type = dataType ?? '';
        if (QlBuilderRelativeDate.isDateDataType(type)) {
            return 'date';
        }
        if (/Boolean|boolean/.test(type)) {
            return 'checkbox';
        }
        if (/Integer|Long|Short|^int$|^long$/.test(type)) {
            return 'numberInt';
        }
        if (/Double|Float|BigDecimal|^double$|^float$/.test(type)) {
            return 'numberDecimal';
        }
        return 'text';
    }

    /**
     * The QL-sourced `WidgetData` the condition builder reads: `dataSource 'ql'` and a request whose first
     * query carries the node hierarchy — reduced to the FILTERABLE nodes (no onlyFirst subtree, no
     * extended/side-car leaf), so the field tree offers exactly the aliases a query-level filter may name.
     */
    static createConditionsWidgetData(model: QlBuilderModel): WidgetData {
        const prune = <T extends QlBuilderContainerNode>(node: T): T => ({
            ...node,
            children: (node.children ?? [])
                .filter(child => child.kind === 'join' && !child.onlyFirst)
                .map(child => prune(child as QlBuilderJoinNode)),
        });
        const request = new QlRequestDto();
        request.queries = [QlBuilderQueryConverter.toQlQuery({...model, root: prune(model.root)})];
        const widgetData = new WidgetData();
        widgetData.dataSource = 'ql';
        widgetData.dataProvider = 'list';
        widgetData.dataProviderObject = model.root.entityName;
        widgetData.dataProviderListRequest = request;
        return widgetData;
    }

    /**
     * Where a set of conditions bites harder than a filter on the node would (shown in the card and live in the
     * dialog): a leaf on a LEFT join alias (or below one) outside an OR group and not `IS NULL`/`IS NOT NULL`
     * drops every row without a linked entry ({@link TEXT_CONDITION_LEFT_JOIN}); a leaf on a 1:n / n:m alias
     * (or below one) can multiply the rows ({@link TEXT_CONDITION_ONE_TO_N}). Each list names the joins once.
     */
    hintsFor(conditions: FilterCriteria[]): QlBuilderConditionHints {
        const model = this.model;
        const hints: QlBuilderConditionHints = {leftJoin: [], oneToN: []};
        if (!model) {
            return hints;
        }
        const info = QlBuilderKeyLabels.nodeInfo(model, this.currentLabelMetas());
        const nameOf = (node: QlBuilderContainerNode): string => `${info.get(node.alias)?.label || node.alias} (${node.alias})`;
        const leftJoin = new Set<string>();
        const oneToN = new Set<string>();

        const visitLeaf = (leaf: FilterCriteria, inOr: boolean): void => {
            const alias = QlBuilderQueryConverter.filterAliasOf(leaf.field);
            if (!alias) {
                return;
            }
            const isNullCheck = leaf.operation === FilterCriteria.cOperatorIsNull || leaf.operation === FilterCriteria.cOperatorIsNotNull;
            for (const node of QlBuilderTreeOps.pathTo(model, alias)) {
                if (node.kind !== 'join') {
                    continue;
                }
                if (node.joinType === 'left' && !inOr && !isNullCheck) {
                    leftJoin.add(nameOf(node));
                }
                if (node.relation === 'oneToN' || node.relation === 'manyToN') {
                    oneToN.add(nameOf(node));
                }
            }
        };
        const walk = (criterion: FilterCriteria, inOr: boolean): void => {
            if (!criterion) {
                return;
            }
            if (criterion.field) {
                visitLeaf(criterion, inOr);
                return;
            }
            (criterion.and ?? []).forEach(member => walk(member, inOr));
            const or = criterion.or ?? [];
            or.forEach(member => walk(member, inOr || or.length > 1));
        };
        conditions.forEach(c => walk(c, false));

        hints.leftJoin = Array.from(leftJoin);
        hints.oneToN = Array.from(oneToN);
        return hints;
    }

    /** `Instant`/`LocalDateTime` attributes (of the field's alias) get whole-day filters; everything else stays untouched. */
    private conditionDateTimeKind(qualifiedField: string): 'instant' | 'dateTime' | null {
        const dataType = this.conditionAttribute(qualifiedField)?.dataType ?? '';
        if (/Instant/.test(dataType)) {
            return 'instant';
        }
        return /LocalDateTime/.test(dataType) ? 'dateTime' : null;
    }

    /** The metadata attribute behind `<alias>.<field>` (plain name or scalar QL name), `null` when unknown. */
    private conditionAttribute(qualifiedField: string): MetaDataAttributeDto | null {
        const alias = QlBuilderQueryConverter.filterAliasOf(qualifiedField);
        if (!alias || !this.model) {
            return null;
        }
        const bare = qualifiedField.substring(alias.length + 1);
        const metas = this.currentLabelMetas();
        const entityAlias = QlBuilderKeyLabels.nodeInfo(this.model, metas).get(alias)?.entityAlias;
        const meta = entityAlias ? metas.get(entityAlias) : null;
        return meta?.attributes?.find(a => a.attributeName === bare || MetaDataAttributeKind.scalarFieldName(a) === bare) ?? null;
    }

    /** "Typ: Name" for a join field, "Name" for a root field; the raw key when the metadata is unknown. */
    private conditionFieldLabel(qualifiedField: string): string {
        const alias = QlBuilderQueryConverter.filterAliasOf(qualifiedField);
        if (!alias || !this.model) {
            return qualifiedField;
        }
        const metas = this.currentLabelMetas();
        const info = QlBuilderKeyLabels.nodeInfo(this.model, metas).get(alias);
        if (!info) {
            return qualifiedField;
        }
        const bare = qualifiedField.substring(alias.length + 1);
        const meta = info.entityAlias ? metas.get(info.entityAlias) ?? null : null;
        const attribute = this.conditionAttribute(qualifiedField);
        const fieldLabel = attribute?.label || QlBuilderKeyLabels.memberLabel(meta, bare) || bare;
        return info.label ? `${info.label}: ${fieldLabel}` : fieldLabel;
    }

    /** Enum values as their value-list label (per alias), dates as `dd.MM.yyyy`, multi-values element by element. */
    private conditionValueLabel(qualifiedField: string, value: unknown): string {
        const attribute = this.conditionAttribute(qualifiedField);
        const labelOf = (single: unknown): string | null =>
            attribute?.valueListEntries?.find(e => String(e.key) === String(single).trim())?.label || null;
        if (Array.isArray(value)) {
            return value.map(v => labelOf(v) ?? QlBuilderNodeComponent.formatFilterValue(v)).join(', ');
        }
        const single = labelOf(value);
        if (single) {
            return single;
        }
        if (typeof value === 'string' && value.includes(',') && attribute?.valueListEntries?.length) {
            const labels = value.split(',').map(labelOf);
            if (labels.every(l => !!l)) {
                return labels.join(', ');
            }
        }
        return QlBuilderNodeComponent.formatFilterValue(value);
    }

    private recomputeConditions(model: QlBuilderModel | null): void {
        const conditions = model?.conditions ?? [];
        const resolver = {
            fieldLabel: (field: string) => this.conditionFieldLabel(field),
            valueLabel: (field: string, value: unknown) => this.conditionValueLabel(field, value),
        };
        const plain = conditions.filter(c => !QlBuilderRelativeDate.isRelativeCriterion(c));
        this.conditionsSummary = QlBuilderNodeComponent.foldDayRanges(plain)
            .map(c => QlBuilderNodeComponent.describeCriteria(c, resolver));

        const offeredBases = new Set(QlBuilderRelativeDate.BASE_OPTIONS.map(option => option.value));
        this.conditionDateFieldOptions = this.buildConditionDateFieldOptions(model);
        const chips: QlBuilderRelativeFilterChip[] = [];
        conditions.forEach((criterion, index) => {
            if (!QlBuilderRelativeDate.isRelativeCriterion(criterion)) {
                return;
            }
            const option = this.conditionDateFieldOptions.find(o => o.value === criterion.field);
            const expression = QlBuilderRelativeDate.parse(criterion.value);
            chips.push({
                index,
                label: QlBuilderRelativeDate.describeCriterion(criterion, option?.label ?? this.conditionFieldLabel(criterion.field))
                    ?? String(criterion.value),
                value: String(criterion.value),
                editable: !!option && !!expression && offeredBases.has(expression.base) && !!QlBuilderRelativeDate.toAmount(expression),
            });
        });
        this.conditionRelativeChips = chips;
        this.conditionHints = this.hintsFor(conditions);
        if (!this.conditionsDraft) {
            this.conditionDialogHints = this.conditionHints;
        }
        this.filterableNodeCount = QlBuilderTreeOps.filterableNodes(model).length;
    }

    /** Date fields of every filterable node, qualified values, "Typ: Erstellt am" labels for join fields. */
    private buildConditionDateFieldOptions(model: QlBuilderModel | null): QlBuilderFieldOption[] {
        const options: QlBuilderFieldOption[] = [];
        if (!model) {
            return options;
        }
        const metas = this.currentLabelMetas();
        const info = QlBuilderKeyLabels.nodeInfo(model, metas);
        for (const node of QlBuilderTreeOps.filterableNodes(model)) {
            const nodeInfo = info.get(node.alias);
            const meta = nodeInfo?.entityAlias ? metas.get(nodeInfo.entityAlias) : null;
            for (const attribute of meta?.attributes ?? []) {
                if (MetaDataAttributeKind.isExtendedAttribute(attribute) || !QlBuilderRelativeDate.isDateDataType(attribute.dataType)) {
                    continue;
                }
                const label = attribute.label || attribute.attributeName;
                options.push({
                    label: nodeInfo.label ? `${nodeInfo.label}: ${label}` : label,
                    value: `${node.alias}.${MetaDataAttributeKind.scalarFieldName(attribute)}`,
                    group: null,
                });
            }
        }
        return options;
    }

    // ------------------------------------------------------------------
    // limit
    // ------------------------------------------------------------------

    onLimitChange(limit: number | null): void {
        if (!this.model || limit == null) {
            return;
        }
        const clamped = Math.max(1, Math.min(Math.round(limit), this.maxLimit));
        if (clamped !== this.model.limit) {
            this.emit({...this.model, limit: clamped});
        }
    }

    // ------------------------------------------------------------------
    // sorting
    // ------------------------------------------------------------------

    addSorting(): void {
        if (!this.model || !this.sortOptions.length) {
            return;
        }
        const used = new Set((this.model.sortings ?? []).map(s => s.name));
        const next = this.sortOptions.find(o => !used.has(o.value)) ?? this.sortOptions[0];
        this.emit({...this.model, sortings: [...(this.model.sortings ?? []), new Sorting(next.value, true)]});
    }

    updateSorting(index: number, name: string | null, ascending: boolean | null): void {
        if (!this.model?.sortings?.[index]) {
            return;
        }
        const current = this.model.sortings[index];
        const sortings = [...this.model.sortings];
        sortings[index] = new Sorting(name ?? current.name, ascending ?? current.ascending);
        this.emit({...this.model, sortings});
    }

    removeSorting(index: number): void {
        if (!this.model?.sortings) {
            return;
        }
        this.emit({...this.model, sortings: this.model.sortings.filter((_, i) => i !== index)});
    }

    // ------------------------------------------------------------------
    // grouping mode
    // ------------------------------------------------------------------

    onGroupByToggle(enabled: boolean): void {
        if (!this.model) {
            return;
        }
        if (enabled) {
            if (!QlBuilderTreeOps.enableGroupByBlockedReason(this.model)) {
                // remembered BEFORE the switch: enableGroupBy clears the sortings (they are column-bound)
                this.groupByHint = {
                    sortingsRemoved: (this.model.sortings?.length ?? 0) > 0,
                    joinKeys: QlBuilderTreeOps.joinFieldKeys(this.model),
                };
                this.emit(QlBuilderTreeOps.enableGroupBy(this.model));
            }
        } else {
            this.groupByHint = null;
            this.emit(QlBuilderTreeOps.disableGroupBy(this.model));
        }
    }

    /** "Join-Felder entfernen" (QA #5): the joins stay, only their fields - the extra grouping keys - go. */
    clearJoinFields(): void {
        if (this.model) {
            this.offerEmptyJoinRemoval = true;
            this.emit(QlBuilderTreeOps.clearJoinFields(this.model));
        }
    }

    /** Q3-8 "Leere Verknüpfung entfernen": drops the joins left without fields, filters and children. */
    removeEmptyJoins(): void {
        if (this.model) {
            this.offerEmptyJoinRemoval = false;
            this.emptyJoinHint = null;
            this.emit(QlBuilderTreeOps.removeEmptyJoins(this.model));
        }
    }

    dismissGroupByHint(): void {
        this.groupByHint = null;
    }

    dismissEmptyJoinHint(): void {
        this.offerEmptyJoinRemoval = false;
        this.emptyJoinHint = null;
    }

    addGroupField(): void {
        const groupBy = this.model?.groupBy;
        if (!groupBy || !this.groupFieldOptions.length) {
            return;
        }
        const used = new Set(groupBy.fields.map(f => f.name));
        const option = this.groupFieldOptions.find(o => !used.has(o.value)) ?? this.groupFieldOptions[0];
        this.setGroupFields([...groupBy.fields, {name: option.value}]);
    }

    updateGroupFieldName(index: number, name: string): void {
        const fields = [...(this.model?.groupBy?.fields ?? [])];
        if (!fields[index] || !name) {
            return;
        }
        // a new attribute starts as a plain column - a dateGroup only makes sense on the date it was set for
        fields[index] = {name};
        this.setGroupFields(fields);
    }

    updateGroupFieldPrecision(index: number, precision: QlBuilderDateGroupPrecision | null): void {
        const fields = [...(this.model?.groupBy?.fields ?? [])];
        const field = fields[index];
        if (!field) {
            return;
        }
        if (!precision) {
            fields[index] = {name: field.name};
        } else {
            const taken = this.takenAs(index, 'field');
            fields[index] = {
                name: field.name,
                as: QlBuilderTreeOps.uniqueAs(`${field.name}_${precision}`, taken),
                dateGroup: {precision},
            };
        }
        this.setGroupFields(fields);
    }

    removeGroupField(index: number): void {
        this.setGroupFields((this.model?.groupBy?.fields ?? []).filter((_, i) => i !== index));
    }

    addAggregate(): void {
        const groupBy = this.model?.groupBy;
        if (!groupBy) {
            return;
        }
        const taken = this.takenAs(-1, 'aggregate');
        this.setAggregates([...groupBy.aggregates, {name: '*', fn: 'count', as: QlBuilderTreeOps.uniqueAs('count', taken)}]);
    }

    updateAggregate(index: number, fn: QlBuilderGroupByAggregateFunction | null, name: string | null): void {
        const aggregates = [...(this.model?.groupBy?.aggregates ?? [])];
        const current = aggregates[index];
        if (!current) {
            return;
        }
        const nextFn = fn ?? current.fn;
        let nextName = name ?? current.name;
        if (nextFn !== 'count' && nextName === '*') {
            nextName = this.aggregateFieldOptions(nextFn)[0]?.value ?? '*';
        }
        if (nextFn !== 'count' && nextName === '*') {
            return; // no numeric field available for sum/avg/min/max
        }
        const taken = this.takenAs(index, 'aggregate');
        const base = nextName === '*' ? nextFn : `${nextFn}_${nextName}`;
        aggregates[index] = {name: nextName, fn: nextFn, as: QlBuilderTreeOps.uniqueAs(base, taken)};
        this.setAggregates(aggregates);
    }

    removeAggregate(index: number): void {
        this.setAggregates((this.model?.groupBy?.aggregates ?? []).filter((_, i) => i !== index));
    }

    /** Field options of one aggregate function: `*` only for count; sum/avg numeric; min/max numeric or date. */
    aggregateFieldOptions(fn: QlBuilderGroupByAggregateFunction): QlBuilderPanelOption<string>[] {
        const plain = this.groupFieldOptions.filter(o => !o.value.startsWith('g_') && !this.isRootJoinName(o.value));
        switch (fn) {
            case 'count':
                return [{label: 'alle Datensätze (*)', value: '*'}, ...plain.map(o => ({label: o.label, value: o.value}))];
            case 'sum':
            case 'avg':
                return plain.filter(o => o.isNumeric).map(o => ({label: o.label, value: o.value}));
            default:
                return plain.filter(o => o.isNumeric || o.isDate).map(o => ({label: o.label, value: o.value}));
        }
    }

    isDateField(name: string): boolean {
        return !!this.groupFieldOptions.find(o => o.value === name)?.isDate;
    }

    // ------------------------------------------------------------------
    // internals
    // ------------------------------------------------------------------

    private setGroupFields(fields: QlBuilderGroupByFieldModel[]): void {
        if (this.model?.groupBy) {
            this.emit({...this.model, groupBy: {...this.model.groupBy, fields}, sortings: this.pruneSortings(fields, null)});
        }
    }

    private setAggregates(aggregates: QlBuilderGroupByAggregateModel[]): void {
        if (this.model?.groupBy) {
            this.emit({...this.model, groupBy: {...this.model.groupBy, aggregates}, sortings: this.pruneSortings(null, aggregates)});
        }
    }

    /** Sortings on a grouping column/aggregate that no longer exists would break the query. */
    private pruneSortings(fields: QlBuilderGroupByFieldModel[] | null, aggregates: QlBuilderGroupByAggregateModel[] | null): Sorting[] {
        const model = this.model;
        const groupBy = {
            fields: fields ?? model.groupBy.fields,
            aggregates: aggregates ?? model.groupBy.aggregates,
        };
        const valid = new Set(QlBuilderTreeOps.sortableKeys({...model, groupBy}));
        return (model.sortings ?? []).filter(s => valid.has(s.name));
    }

    private takenAs(exceptIndex: number, kind: 'field' | 'aggregate'): string[] {
        const groupBy = this.model?.groupBy;
        if (!groupBy) {
            return [];
        }
        return [
            ...groupBy.fields.filter((_, i) => kind !== 'field' || i !== exceptIndex).map(f => f.as || f.name),
            ...groupBy.aggregates.filter((_, i) => kind !== 'aggregate' || i !== exceptIndex).map(a => a.as),
        ];
    }

    private isRootJoinName(name: string): boolean {
        return !!this.rootMeta?.joins?.some(j => j.name === name);
    }

    private emit(model: QlBuilderModel | null): void {
        this.modelChange.emit(model);
    }

    private recompute(): void {

        const model = this.model;

        this.envBound = model ? QlBuilderQueryConverter.deriveEnvBound(model) : false;
        this.rootContext = model ? QlBuilderTreeOps.rootContext(model) : null;
        this.groupByBlockedReason = QlBuilderTreeOps.enableGroupByBlockedReason(model);
        if (this.groupByHint) {
            const joinKeys = model?.groupBy ? QlBuilderTreeOps.joinFieldKeys(model) : [];
            this.groupByHint = !model?.groupBy || (!joinKeys.length && !this.groupByHint.sortingsRemoved)
                ? null
                : {...this.groupByHint, joinKeys};
        }
        this.recomputeEmptyJoinHint(model);
        this.maxLimit = model?.groupBy ? QL_BUILDER_POLICY_LIMITS.maxPagingGroupBy : QL_BUILDER_POLICY_LIMITS.maxPagingEntity;

        const keys = QlBuilderTreeOps.sortableKeys(model);
        const current = (model?.sortings ?? []).map(s => s.name).filter(name => !keys.includes(name));
        const metas = this.currentLabelMetas();
        const labels = QlBuilderKeyLabels.labels(model, metas, this.sideCarFieldLabels);
        this.sortOptions = [...keys, ...current].map(key => ({label: QlBuilderKeyLabels.labelOf(labels, key), value: key}));
        this.requestLabelMetas(model, metas);
        this.requestSideCarLabels(model);
        this.recomputeConditions(model);
        this.prepareConditionsDialog();

        this.groupFieldOptions = this.buildGroupFieldOptions(this.rootMeta, model);
        this.aggregateOptionsByFn = {
            count: this.aggregateFieldOptions('count'),
            sum: this.aggregateFieldOptions('sum'),
            avg: this.aggregateFieldOptions('avg'),
            min: this.aggregateFieldOptions('min'),
            max: this.aggregateFieldOptions('max'),
        };
    }

    /** The root metadata plus every joined entity's metadata loaded so far. */
    private recomputeEmptyJoinHint(model: QlBuilderModel | null): void {
        const joinKeyCount = model?.groupBy ? QlBuilderTreeOps.joinFieldKeys(model).length : 0;
        if (this.lastJoinKeyCount > 0 && joinKeyCount === 0 && model?.groupBy) {
            this.offerEmptyJoinRemoval = true;
        }
        this.lastJoinKeyCount = joinKeyCount;
        const empties = this.offerEmptyJoinRemoval && model?.groupBy ? QlBuilderTreeOps.emptyJoins(model) : [];
        if (!empties.length) {
            this.offerEmptyJoinRemoval = false;
            this.emptyJoinHint = null;
            return;
        }
        const rootJoins = new Set(model.root.children.filter(c => c.kind === 'join').map(c => (c as QlBuilderJoinNode).alias));
        this.emptyJoinHint = empties.map(join => {
            const label = rootJoins.has(join.alias) ? QlBuilderKeyLabels.memberLabel(this.rootMeta, join.name) : join.name;
            return `${label} (${join.alias})`;
        });
    }

    private currentLabelMetas(): Map<string, MetaDataEntityDto> {
        const metas = new Map(this.labelMetas);
        if (this.rootMeta && this.loadedRootAlias) {
            metas.set(this.loadedRootAlias, this.rootMeta);
        }
        return metas;
    }

    /**
     * Loads (once per entity, cached by `MetaService`) the metadata of joined entities whose labels are
     * still unknown; every arrival recomputes, which may resolve and request the next tree level. A failed
     * load only leaves the raw key as label.
     */
    private requestLabelMetas(model: QlBuilderModel | null, metas: ReadonlyMap<string, MetaDataEntityDto>): void {
        for (const alias of QlBuilderKeyLabels.entityAliases(model, metas)) {
            if (alias === this.loadedRootAlias || metas.has(alias) || this.labelMetaRequested.has(alias)) {
                continue;
            }
            this.labelMetaRequested.add(alias);
            this.labelMetaSubscriptions.add(
                this.metaService.getByAlias(alias, true, true, true, true, true).subscribe({
                    next: meta => {
                        this.labelMetas.set(alias, meta);
                        this.recompute();
                    },
                    error: () => {
                        // label falls back to the key; the conditions dialog stops waiting for this entity
                        this.labelMetaFailed.add(alias);
                        this.prepareConditionsDialog();
                    },
                })
            );
        }
    }

    /**
     * Loads (once per version) the TypeField labels of side-cars the model names only by id; every arrival
     * recomputes the sort options. The join name is used as instance alias (identical for every instance
     * known today); a failing call only leaves "Zusatzfeld <id>" as label.
     */
    private requestSideCarLabels(model: QlBuilderModel | null): void {
        for (const sideCar of QlBuilderKeyLabels.sideCarsWithoutLabels(model, this.sideCarFieldLabels)) {
            const key = QlBuilderKeyLabels.sideCarKey(sideCar.instanceAlias, sideCar.scopeId);
            if (this.sideCarLabelRequested.has(key)) {
                continue;
            }
            this.sideCarLabelRequested.add(key);
            this.labelMetaSubscriptions.add(
                this.sideCarService.getFields(sideCar.instanceAlias, sideCar.scopeId).subscribe({
                    next: fields => {
                        this.sideCarFieldLabels.set(key, new Map((fields ?? []).map(field => [field.typeFieldId, field.label])));
                        this.recompute();
                    },
                    error: () => { /* label falls back to "Zusatzfeld <id>" */ },
                })
            );
        }
    }

    private loadRootMeta(alias: string | null): void {

        this.metaSubscription?.unsubscribe();
        this.loadedRootAlias = alias;
        this.rootMeta = null;

        if (!alias) {
            this.rootMetaState = 'none';
            return;
        }

        this.rootMetaState = 'loading';
        this.metaSubscription = this.metaService.getByAlias(alias, true, true, true, true, true).subscribe({
            next: meta => {
                this.rootMeta = meta;
                this.rootMetaState = 'ready';
                this.recompute();
            },
            error: () => {
                this.rootMetaState = 'error';
                this.recompute();
            },
        });
    }

    /**
     * Grouping columns of the start entity: plain attributes, n:1 relationships (grouped by the FK,
     * named without `Id`) and scalar generic fields (`g_<id>:value`). Object-typed generic fields are
     * never offered — spike S3: every object suffix fails as a groupBy column, `:id` included
     * (policy `objectGenericFieldInGroupBy`). Computed/extended members never.
     */
    private buildGroupFieldOptions(meta: MetaDataEntityDto | null, model: QlBuilderModel | null): QlBuilderPanelFieldOption[] {

        const options: QlBuilderPanelFieldOption[] = [];

        for (const attribute of meta?.attributes ?? []) {
            if (MetaDataAttributeKind.isExtendedAttribute(attribute) || MetaDataAttributeKind.isEavAttribute(attribute)) {
                continue;
            }
            const label = attribute.label || attribute.attributeName;
            if (MetaDataAttributeKind.isGenericAttribute(attribute)) {
                options.push({
                    label: `${label} (generisch)`,
                    value: MetaDataAttributeKind.scalarFieldName(attribute),
                    isDate: false,
                    isNumeric: false,
                });
                continue;
            }
            options.push({
                label: label === attribute.attributeName ? label : `${label} (${attribute.attributeName})`,
                value: attribute.attributeName,
                isDate: QlBuilderPanelComponent.isDateType(attribute),
                isNumeric: QlBuilderPanelComponent.isNumericType(attribute),
            });
        }

        for (const join of meta?.joins ?? []) {
            if (join.extendedService || join.extendedConfig) {
                continue;
            }
            if (join.generic) {
                continue; // object-typed generic field: not groupable (S3)
            }
            if (join.joinRelationEnum === MetaDataJoinRelationEnum.manyToOne && join.name) {
                options.push({label: `${join.label || join.name} (Verknüpfung)`, value: join.name, isDate: false, isNumeric: false});
            }
        }

        // keep names the model already groups by visible, even if meta does not (yet) know them
        for (const field of model?.groupBy?.fields ?? []) {
            if (!options.some(o => o.value === field.name)) {
                options.push({label: field.name, value: field.name, isDate: !!field.dateGroup, isNumeric: false});
            }
        }

        return options;
    }

    private static isDateType(attribute: MetaDataAttributeDto): boolean {
        return /(LocalDate|LocalDateTime|Instant|OffsetDateTime|ZonedDateTime|java\.util\.Date|java\.sql\.(Date|Timestamp))/.test(attribute.dataType ?? '');
    }

    private static isNumericType(attribute: MetaDataAttributeDto): boolean {
        return /(Integer|Long|Short|Double|Float|BigDecimal|BigInteger|^int$|^long$|^double$|^float$|^short$)/.test(attribute.dataType ?? '');
    }
}
